declare global {
  interface Window {
    googletag?: googletag.Googletag
    OnetrustActiveGroups?: string
  }
}

export const useGoogleAd = (
  idRef: MaybeRef<string>,
  unitRef: MaybeRef<string>,
  sizeMappingRef?: MaybeRef<googletag.SizeMappingArray>,
  onRender?: (event: googletag.events.SlotRenderEndedEvent) => void,
) => {
  if (typeof window === 'undefined') {
    return { isLoaded: ref(false) }
  }

  window.googletag = window.googletag || { cmd: [] } as unknown as googletag.Googletag
  const { googletag } = window
  const route = useRoute()

  const { load, status } = useScript({
    src: 'https://securepubads.g.doubleclick.net/tag/js/gpt.js',
    async: true,
    crossorigin: 'anonymous',
  })

  const isLoaded = computed<boolean>(() => status.value === 'loaded')

  /**
   * Destroy an existing ad slot if it already exists
   */
  const destroyExistingSlot = async (id: string): Promise<void> => {
    return new Promise((resolve) => {
      if (!googletag || !googletag.pubadsReady) {
        return resolve()
      }

      googletag.cmd.push(() => {
        try {
          googletag.pubads().setForceSafeFrame(true)

          const existingSlot = googletag.pubads().getSlots().find(slot => slot.getSlotElementId() === id)
          if (existingSlot) {
            googletag.destroySlots([existingSlot])
          }
        }
        catch (error) {
          console.error('[GAM]: Error destroying ad slots:', error)
        }
        setTimeout(resolve, 100) // Small delay to ensure destruction is processed
      })
    })
  }

  /**
   * Define and display a Google Ad slot
   */
  const defineSlots = async () => {
    const id = unref(idRef)
    const unit = unref(unitRef)
    const sizeMapping = unref(sizeMappingRef)

    if (!id || !unit) {
      console.error('[GAM]: Missing required `id` or `unit`.')
      return
    }

    if (!sizeMapping || sizeMapping.length === 0) {
      console.error('[GAM]: Invalid sizeMapping detected, skipping slot definition.')
      return
    }

    await destroyExistingSlot(id) // Ensure old slot is removed before redefining

    await nextTick()

    googletag.cmd.push(async () => {
      try {
        await nextTick()

        googletag.pubads().setForceSafeFrame(true)

        const existingSlot = googletag.pubads().getSlots().find(slot => slot.getSlotElementId() === id)
        if (existingSlot) {
          return
        }

        // Define a new ad slot with the first size as default
        const defaultSize = [[728, 90], [300, 50], 'fluid']
        const slot = googletag.defineSlot(unit, defaultSize, id).addService(googletag.pubads())

        if (!slot) {
          console.error(`[GAM]: Failed to define slot for ${unit}`)
          return
        }

        slot.defineSizeMapping(sizeMapping)

        googletag.pubads().enableSingleRequest()
        googletag.enableServices()
        googletag.display(id)

        // Track when the ad is rendered
        googletag.pubads().addEventListener('slotRenderEnded', (event) => {
          if (event.slot.getSlotElementId() === id) {
            onRender?.(event)
          }
        })
      }
      catch (error) {
        console.error('[GAM]: Error defining slots:', error)
      }
    })
  }

  /**
   * Refresh existing ad slots on route change
   */
  const refreshAds = async () => {
    const id = unref(idRef)

    await destroyExistingSlot(id)
    await defineSlots()
  }

  watch(isLoaded, (newValue) => {
    if (newValue) {
      defineSlots()
    }
  })

  watch(() => route.fullPath, async () => {
    await refreshAds()
  })

  onMounted(async () => {
    try {
      await load()
      await defineSlots()
    }
    catch (error) {
      console.error('[GAM]: Error loading script:', error)
    }
  })

  onBeforeUnmount(async () => {
    const id = unref(idRef)
    await destroyExistingSlot(id)
  })

  return { isLoaded }
}
